/**
 * Free-text `text` primitive for the Monitor wizard.
 *
 * Renders a single-line input for capability fields declared as `type: text`
 * — currently DataEQ's `extra_filter` (an optional DataEQ filter DSL
 * expression). Without this primitive the FieldRenderer fell back to the
 * "Unsupported field type" warning for any text field, and the user
 * couldn't enter free-form values for those fields.
 *
 * Keep deliberately bare — no validation, no debounce. Wizard semantics:
 * we forward whatever the user types straight into the per-monitor
 * source_config so a back-end with its own filter language doesn't have
 * to roundtrip through a frontend parser. If a SKILL needs richer text
 * (multi-line, regex hints, autocomplete), a sibling primitive like
 * TextAreaField is the cleaner path than overloading this one.
 */
import React from 'react';

import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import type { CapabilityField } from '../../../../../types/importCapabilities';

interface TextFieldProps {
  field: CapabilityField;
  value: string | undefined;
  onChange: (value: string) => void;
}

const TextField: React.FC<TextFieldProps> = ({ field, value, onChange }) => {
  const fieldId = `field-${field.id}`;
  const initial =
    typeof value === 'string'
      ? value
      : typeof field.default === 'string'
        ? field.default
        : '';

  return (
    <div className="space-y-1.5">
      <Label htmlFor={fieldId}>
        {field.label || field.id}
        {field.required ? <span className="ml-1 text-red-500">*</span> : null}
      </Label>
      {field.description ? (
        <p className="text-xs text-slate-500">{field.description}</p>
      ) : null}
      <Input
        id={fieldId}
        type="text"
        value={initial}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          typeof field.default === 'string' ? field.default : undefined
        }
      />
    </div>
  );
};

export default TextField;
